import type { BufferGeometry } from "three";

export interface ResourceLease<T> {
  value: T;
  release(): void;
}

type Entry = {
  geometry: BufferGeometry;
  references: number;
  bytes: number;
};

/**
 * Owns immutable, fully processed geometries. Live leases are never evicted;
 * only unused entries participate in the count/byte-bounded LRU.
 */
export class FinishedGeometryCache {
  private readonly entries = new Map<string, Entry>();
  private readonly idle = new Map<string, Entry>();
  private idleBytes = 0;

  constructor(
    private readonly maxIdleEntries = 32,
    private readonly maxIdleBytes = 32 * 1024 * 1024,
  ) {}

  /**
   * Generic in the geometry type so a caller that stored a subclass gets it
   * back — `ProfiledContourGeometry` carries the developed section width its
   * material needs, and widening that to `BufferGeometry` here would throw it
   * away. The cast rests on one instance of this cache holding one kind of
   * geometry — `profileGeometryCache` is reached only from `ProfileRenderer`
   * and `panelGeometryCache` only from `PanelRenderer` — and not on the keys,
   * which are per-instance and so could not separate two types sharing a cache
   * anyway. A second producer on either instance would have to keep that up.
   */
  acquire<T extends BufferGeometry>(
    key: string,
    create: () => T,
  ): ResourceLease<T> {
    let entry = this.entries.get(key);
    if (!entry) {
      const geometry = create();
      const buffers = new Set<ArrayBufferLike>();
      for (const attribute of Object.values(geometry.attributes)) {
        const typed = attribute;
        const array =
          "isInterleavedBufferAttribute" in typed
            ? typed.data.array
            : typed.array;
        buffers.add(array.buffer);
      }
      if (geometry.index) buffers.add(geometry.index.array.buffer);
      entry = {
        geometry,
        references: 0,
        bytes: [...buffers].reduce((sum, buffer) => sum + buffer.byteLength, 0),
      };
      this.entries.set(key, entry);
    }
    if (this.idle.delete(key)) this.idleBytes -= entry.bytes;
    entry.references += 1;
    let released = false;
    return {
      value: entry.geometry as T,
      release: () => {
        if (released) return;
        released = true;
        entry.references -= 1;
        if (entry.references !== 0) return;
        this.idle.set(key, entry);
        this.idleBytes += entry.bytes;
        this.trim();
      },
    };
  }

  private trim(): void {
    for (const [key, entry] of this.idle) {
      if (
        this.idle.size <= this.maxIdleEntries &&
        this.idleBytes <= this.maxIdleBytes
      )
        break;
      this.evict(key, entry);
    }
  }

  private evict(key: string, entry: Entry): void {
    this.idle.delete(key);
    this.entries.delete(key);
    this.idleBytes -= entry.bytes;
    entry.geometry.dispose();
  }

  /** Explicit cache teardown cannot invalidate geometry still attached elsewhere. */
  clearIdle(): void {
    for (const [key, entry] of this.idle) this.evict(key, entry);
  }

  get stats() {
    return {
      entries: this.entries.size,
      activeEntries: this.entries.size - this.idle.size,
      idleEntries: this.idle.size,
      idleBytes: this.idleBytes,
      references: [...this.entries.values()].reduce(
        (sum, entry) => sum + entry.references,
        0,
      ),
    };
  }
}

export const panelGeometryCache = new FinishedGeometryCache();
export const profileGeometryCache = new FinishedGeometryCache();
/**
 * Pad bodies, and their outlines separately.
 *
 * Two instances rather than one keyed pair, for the same reason the two above
 * are separate: a pad body is tens of thousands of triangles and its outline is
 * a plain box, so letting the two compete for one byte budget would let a
 * handful of bodies evict every outline in the scene.
 */
export const padGeometryCache = new FinishedGeometryCache();
export const padOutlineCache = new FinishedGeometryCache();
